import { type Checkpoint } from '../../shared/sync/schemas';
import { type ItemDoc } from '../rxdb/collections/items';
import { useDatabase } from '../rxdb/provider';
import { startItemsReplication } from '../rxdb/replication/items';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type RxReplicationState } from 'rxdb/plugins/replication';

export const useProcessItems = (processId: string) => {
  const { db, userId } = useDatabase();
  const [items, setItems] = useState<ItemDoc[]>([]);
  const [syncedProcessIds, setSyncedProcessIds] = useState<Set<string>>(
    () => new Set(),
  );
  const replicationRef = useRef<null | RxReplicationState<ItemDoc, Checkpoint>>(
    null,
  );

  useEffect(() => {
    const replication = startItemsReplication(db, userId, processId);
    replicationRef.current = replication;
    let cancelled = false;

    const waitForInitialSync = async () => {
      await replication.awaitInitialReplication();
      if (!cancelled) {
        setSyncedProcessIds((current) => new Set(current).add(processId));
      }
    };

    void waitForInitialSync();

    const sub = db.items
      .find({
        selector: { processId },
        sort: [{ createdAt: 'asc' }, { id: 'asc' }],
      })
      .$.subscribe((docs) => {
        setItems(docs.map((doc) => doc.toJSON()));
      });

    return () => {
      cancelled = true;
      sub.unsubscribe();
      replicationRef.current = null;
      void replication.cancel();
    };
  }, [db, processId, userId]);

  const reSync = useCallback(() => {
    replicationRef.current?.reSync();
  }, []);

  const todos = items.filter((item) => item.type === 'TODO');
  const timeline = items.filter(
    (item) => item.type === 'COMMENT' || item.type === 'FILE',
  );
  const isInitialSyncing = !syncedProcessIds.has(processId);

  return { isInitialSyncing, items, reSync, timeline, todos };
};
